import React, { useMemo, useState } from 'react';
import { Gift, Search, X } from 'lucide-react';
import { GiftCatalogProduct } from '../../../types/gifticon';

type SortKey = 'priceAsc' | 'priceDesc' | 'name';

const point = (value: number) => `${value.toLocaleString()}P`;

const ProductSelectModal = ({
    products,
    isLoading,
    isError,
    onSelect,
    onClose,
}: {
    /** 서버 상품 목록 (GET /products) — 정적 카탈로그는 쓰지 않는다 */
    products: GiftCatalogProduct[] | null;
    isLoading: boolean;
    isError: boolean;
    onSelect: (product: GiftCatalogProduct) => void;
    onClose: () => void;
}) => {
    const [category, setCategory] = useState('전체');
    const [brand, setBrand] = useState('전체');
    const [keyword, setKeyword] = useState('');
    const [sort, setSort] = useState<SortKey>('priceAsc');

    const allProducts = useMemo(() => products ?? [], [products]);
    const categories = useMemo(
        () => ['전체', ...Array.from(new Set(allProducts.map((product) => product.category).filter(Boolean)))],
        [allProducts],
    );
    const brands = useMemo(
        () => ['전체', ...Array.from(new Set(allProducts.map((product) => product.brand).filter(Boolean)))],
        [allProducts],
    );

    const visibleProducts = useMemo(() => {
        const filtered = allProducts.filter((product) => {
            if (category !== '전체' && product.category !== category) return false;
            if (brand !== '전체' && product.brand !== brand) return false;
            const query = keyword.trim();
            if (query && !`${product.brand} ${product.name}`.includes(query)) return false;
            return true;
        });

        return [...filtered].sort((a, b) => {
            if (sort === 'priceAsc') return a.price - b.price;
            if (sort === 'priceDesc') return b.price - a.price;
            return a.name.localeCompare(b.name, 'ko');
        });
    }, [allProducts, category, brand, keyword, sort]);

    // 상품 목록이 비면 정적 카탈로그로 대체하지 않고 로딩·실패 상태를 그대로 알린다
    const emptyMessage = isLoading
        ? '상품을 불러오는 중입니다…'
        : isError
            ? '상품을 불러오지 못했어요. 잠시 후 다시 시도해 주세요.'
            : allProducts.length === 0
                ? '보낼 수 있는 상품이 없습니다.'
                : '조건에 맞는 상품이 없습니다.';

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-[rgba(15,23,42,0.38)] px-[24px]">
            <div className="flex max-h-[86vh] w-full max-w-[840px] flex-col rounded-[8px] bg-white p-[24px] shadow-[0_24px_60px_rgba(15,23,42,0.24)]">
                <div className="flex items-start justify-between gap-[16px]">
                    <div>
                        <h2 className="text-[18px] font-bold leading-[26px] text-[#1F2937]">상품 선택</h2>
                        <p className="mt-[2px] text-[13px] leading-[20px] text-[#64748B]">
                            {allProducts.length > 0
                                ? `전체 상품 ${allProducts.length}개 · 원하는 상품을 검색해 선택하세요`
                                : emptyMessage}
                        </p>
                    </div>
                    <button type="button" aria-label="닫기" onClick={onClose} className="rounded-[8px] p-[6px] hover:bg-[#F8FAFC]">
                        <X size={20} color="#64748B" />
                    </button>
                </div>

                <div className="mt-[16px] flex flex-wrap items-center gap-[8px]">
                    <div className="flex flex-wrap gap-[6px]">
                        {categories.map((item) => (
                            <button
                                key={item}
                                type="button"
                                onClick={() => setCategory(item)}
                                className={`h-[32px] rounded-full border px-[12px] text-[12px] font-bold ${
                                    category === item
                                        ? 'border-[#3D73DD] bg-[#ECF1FC] text-[#3D73DD]'
                                        : 'border-[#CBD5E1] bg-white text-[#64748B]'
                                }`}
                            >
                                {item}
                            </button>
                        ))}
                    </div>
                    <select
                        value={brand}
                        onChange={(event) => setBrand(event.target.value)}
                        className="h-[34px] rounded-[8px] border border-[#CBD5E1] bg-white px-[9px] text-[12px] font-bold text-[#475569]"
                    >
                        {brands.map((item) => (
                            <option key={item} value={item}>{item === '전체' ? '브랜드 전체' : item}</option>
                        ))}
                    </select>
                    <select
                        value={sort}
                        onChange={(event) => setSort(event.target.value as SortKey)}
                        className="h-[34px] rounded-[8px] border border-[#CBD5E1] bg-white px-[9px] text-[12px] font-bold text-[#475569]"
                    >
                        <option value="priceAsc">가격 낮은순</option>
                        <option value="priceDesc">가격 높은순</option>
                        <option value="name">이름순</option>
                    </select>
                    <label className="ml-auto flex h-[34px] w-[200px] items-center gap-[7px] rounded-[8px] border border-[#CBD5E1] bg-white px-[10px] focus-within:border-[#3D73DD]">
                        <Search size={15} color="#94A3B8" />
                        <input
                            value={keyword}
                            onChange={(event) => setKeyword(event.target.value)}
                            placeholder="브랜드·상품명 검색"
                            className="min-w-0 flex-1 text-[12px] outline-none"
                        />
                    </label>
                </div>

                <div className="mt-[14px] grid flex-1 grid-cols-3 content-start gap-[10px] overflow-y-auto pr-[4px]">
                    {visibleProducts.map((product) => (
                        <button
                            key={product.productId}
                            type="button"
                            onClick={() => onSelect(product)}
                            className="flex gap-[10px] rounded-[8px] border border-[#E2E8F0] p-[13px] text-left hover:border-[#3D73DD] hover:bg-[#F8FBFF]"
                        >
                            {product.imageUrl ? (
                                <img
                                    src={product.imageUrl}
                                    alt=""
                                    className="h-[52px] w-[52px] flex-shrink-0 rounded-[8px] border border-[#F1F5F9] object-contain"
                                />
                            ) : (
                                <span className="flex h-[52px] w-[52px] flex-shrink-0 items-center justify-center rounded-[8px] bg-[#EAF0FC] text-[#3D73DD]">
                                    <Gift size={22} />
                                </span>
                            )}
                            <span className="min-w-0 flex-1">
                                <p className="truncate text-[13px] font-bold text-[#1F2937]">{product.brand}</p>
                                <p className="mt-[3px] min-h-[36px] text-[12px] leading-[18px] text-[#475569]">{product.name}</p>
                                <p className="mt-[8px] text-[14px] font-bold text-[#111827]">{point(product.price)}</p>
                            </span>
                        </button>
                    ))}
                    {visibleProducts.length === 0 && (
                        <div className="col-span-3 rounded-[8px] border border-[#E2E8F0] bg-[#F8FAFC] px-[14px] py-[32px] text-center text-[13px] text-[#64748B]">
                            {emptyMessage}
                        </div>
                    )}
                </div>

                <p className="mt-[12px] text-right text-[12px] text-[#94A3B8]">{visibleProducts.length}개 상품</p>
            </div>
        </div>
    );
};

export default ProductSelectModal;
